import React, { useState, useEffect } from 'react';
import { useUser, useClerk } from '@clerk/react';
import { useLocation } from 'wouter';
import { motion } from 'framer-motion';
import { ChevronRight, User, BookOpen, CreditCard, Shield, LifeBuoy, CheckCircle2, FileText, ExternalLink, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useProfile } from './ProfileContext';
import { PreflightChecklist } from './PreflightChecklist';

// ── Types ─────────────────────────────────────────────────────────────────────
type SettingsTab = 'profile' | 'preferences' | 'billing' | 'security' | 'support';

const GRADE_OPTIONS = ['K', '1', '2', '3', '4', '5', '6', '7', '8'];
const SUBJECT_OPTIONS = ['Math', 'ELA', 'Science', 'Social Studies', 'History'];
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina',
  'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island',
  'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];
const THEME_OPTIONS = [
  { value: 'ocean', label: 'Ocean Blue' },
  { value: 'forest', label: 'Forest Green' },
  { value: 'sunset', label: 'Sunset' },
  { value: 'lavender', label: 'Lavender' },
  { value: 'coral', label: 'Coral' },
  { value: 'slate', label: 'Slate' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'amber', label: 'Amber' },
  { value: 'teal', label: 'Teal' },
  { value: 'rose', label: 'Rose' },
  { value: 'indigo', label: 'Indigo' },
  { value: 'emerald', label: 'Emerald' },
];
const DURATION_OPTIONS = ['30', '45', '60', '75', '90'];

// ── Root ──────────────────────────────────────────────────────────────────────
export function SettingsPage({ onBack }: { onBack: () => void }) {
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');

  const TABS: { id: SettingsTab; label: string; icon: React.ElementType }[] = [
    { id: 'profile',     label: 'Profile',            icon: User },
    { id: 'preferences', label: 'Teaching Prefs',     icon: BookOpen },
    { id: 'billing',     label: 'Billing',             icon: CreditCard },
    { id: 'security',    label: 'Security',            icon: Shield },
    { id: 'support',     label: 'Support',             icon: LifeBuoy },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack}
          className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all">
          <ChevronRight size={18} className="rotate-180" />
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-slate-100 flex items-center justify-center">
            <User size={18} className="text-slate-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 leading-tight">Settings</h2>
            <p className="text-xs text-slate-500">Manage your profile and preferences</p>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
      >
        {activeTab === 'profile'     && <ProfileTab />}
        {activeTab === 'preferences' && <PreferencesTab />}
        {activeTab === 'billing'     && <BillingTab />}
        {activeTab === 'security'    && <SecurityTab />}
        {activeTab === 'support'     && <SupportTab />}
      </motion.div>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function SettingsCard({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
      <div>
        <h3 className="text-[15px] font-bold text-slate-900">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </div>
  );
}

function SaveButton({ saving, saved, onClick }: { saving: boolean; saved: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      className="h-10 px-5 rounded-xl bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold transition-all flex items-center gap-2"
    >
      {saved ? <><CheckCircle2 size={15} /> Saved</> : saving ? 'Saving…' : 'Save changes'}
    </button>
  );
}

// ── Profile Tab ───────────────────────────────────────────────────────────────
function ProfileTab() {
  const { user } = useUser();
  const { profile, updateProfile } = useProfile();
  const [school, setSchool] = useState('');
  const [district, setDistrict] = useState('');
  const [state, setState] = useState('');
  const [grades, setGrades] = useState<string[]>([]);
  const [subjects, setSubjects] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setSchool(profile.school ?? '');
      setDistrict(profile.district ?? '');
      setState(profile.state ?? '');
      setGrades(profile.grades ?? []);
      setSubjects(profile.subjects ?? []);
    }
  }, [profile]);

  async function handleSave() {
    setSaving(true);
    await updateProfile({ school, district, state, grades, subjects });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function toggleGrade(g: string) {
    setGrades(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
    setSaved(false);
  }

  function toggleSubject(s: string) {
    setSubjects(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
    setSaved(false);
  }

  return (
    <div className="space-y-4">
      {/* Account info (from Clerk — read-only) */}
      <SettingsCard title="Account" description="Managed by your sign-in provider.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">First Name</Label>
            <div className="h-10 px-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center text-sm text-slate-500">
              {user?.firstName || '—'}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Last Name</Label>
            <div className="h-10 px-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center text-sm text-slate-500">
              {user?.lastName || '—'}
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</Label>
            <div className="h-10 px-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center text-sm text-slate-500">
              {user?.primaryEmailAddress?.emailAddress || '—'}
            </div>
          </div>
        </div>
      </SettingsCard>

      {/* School info */}
      <SettingsCard title="School Information" description="Helps TeachBud personalize your content.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="school" className="text-sm font-semibold text-slate-700">School Name</Label>
            <Input id="school" value={school} onChange={e => { setSchool(e.target.value); setSaved(false); }}
              placeholder="Lincoln Elementary" className="h-10 rounded-xl border-slate-200" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="district" className="text-sm font-semibold text-slate-700">School District</Label>
            <Input id="district" value={district} onChange={e => { setDistrict(e.target.value); setSaved(false); }}
              placeholder="Springfield Unified" className="h-10 rounded-xl border-slate-200" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="state" className="text-sm font-semibold text-slate-700">State</Label>
            <select id="state" value={state}
              onChange={e => { setState(e.target.value); setSaved(false); }}
              className="w-full h-10 px-3 bg-white rounded-xl border border-slate-200 text-sm text-slate-900 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all">
              <option value="">Select a state…</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {/* Grades */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-700">Grade Levels You Teach</Label>
          <div className="flex flex-wrap gap-2">
            {GRADE_OPTIONS.map(g => (
              <button key={g} onClick={() => toggleGrade(g)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                  grades.includes(g)
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}>
                {g === 'K' ? 'Kindergarten' : `Grade ${g}`}
              </button>
            ))}
          </div>
        </div>

        {/* Subjects */}
        <div className="space-y-2">
          <Label className="text-sm font-semibold text-slate-700">Subjects You Teach</Label>
          <div className="flex flex-wrap gap-2">
            {SUBJECT_OPTIONS.map(s => (
              <button key={s} onClick={() => toggleSubject(s)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                  subjects.includes(s)
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <SaveButton saving={saving} saved={saved} onClick={handleSave} />
        </div>
      </SettingsCard>
    </div>
  );
}

// ── Teaching Preferences Tab ──────────────────────────────────────────────────
function PreferencesTab() {
  const { profile, updateProfile } = useProfile();
  const [defaultDuration, setDefaultDuration] = useState('45');
  const [defaultLessonTheme, setDefaultLessonTheme] = useState('ocean');
  const [defaultActivityTheme, setDefaultActivityTheme] = useState('ocean');
  const [defaultPresentationTheme, setDefaultPresentationTheme] = useState('ocean');
  const [defaultAssessmentTheme, setDefaultAssessmentTheme] = useState('ocean');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setDefaultDuration(String(profile.defaultDuration ?? 45));
      setDefaultLessonTheme(profile.defaultLessonTheme ?? 'ocean');
      setDefaultActivityTheme(profile.defaultActivityTheme ?? 'ocean');
      setDefaultPresentationTheme(profile.defaultPresentationTheme ?? 'ocean');
      setDefaultAssessmentTheme(profile.defaultAssessmentTheme ?? 'ocean');
    }
  }, [profile]);

  async function handleSave() {
    setSaving(true);
    await updateProfile({
      defaultDuration: Number(defaultDuration),
      defaultLessonTheme, defaultActivityTheme, defaultPresentationTheme, defaultAssessmentTheme,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  function ThemeSelect({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
    return (
      <div className="space-y-1.5">
        <Label className="text-sm font-semibold text-slate-700">{label}</Label>
        <select value={value} onChange={e => { onChange(e.target.value); setSaved(false); }}
          className="w-full h-10 px-3 bg-white rounded-xl border border-slate-200 text-sm text-slate-900 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all">
          {THEME_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <SettingsCard title="Default Lesson Length"
        description="This will pre-fill the duration field each time you create a new lesson.">
        <div className="flex flex-wrap gap-2">
          {DURATION_OPTIONS.map(d => (
            <button key={d} onClick={() => { setDefaultDuration(d); setSaved(false); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                defaultDuration === d
                  ? 'bg-primary text-white border-primary shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
              }`}>
              {d} min
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <SaveButton saving={saving} saved={saved} onClick={handleSave} />
        </div>
      </SettingsCard>

      <SettingsCard title="Default Themes"
        description="These themes will be pre-selected when you create new resources.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ThemeSelect label="Lesson Plan Theme"       value={defaultLessonTheme}       onChange={setDefaultLessonTheme} />
          <ThemeSelect label="Activity Sheet Theme"    value={defaultActivityTheme}     onChange={setDefaultActivityTheme} />
          <ThemeSelect label="Presentation Theme"      value={defaultPresentationTheme} onChange={setDefaultPresentationTheme} />
          <ThemeSelect label="Assessment Theme"        value={defaultAssessmentTheme}   onChange={setDefaultAssessmentTheme} />
        </div>
        <div className="flex justify-end">
          <SaveButton saving={saving} saved={saved} onClick={handleSave} />
        </div>
      </SettingsCard>
    </div>
  );
}

// ── Billing Tab ───────────────────────────────────────────────────────────────
function BillingTab() {
  const [, navigate] = useLocation();

  return (
    <div className="space-y-4">
      <SettingsCard title="Subscription">
        <p className="text-sm text-slate-600 mb-4">
          View your plan, payment method, invoices, next billing date, and time-saved stats.
        </p>
        <Button
          onClick={() => navigate('/account')}
          className="rounded-xl bg-primary hover:bg-primary/90 text-white"
        >
          View Account Status
        </Button>
      </SettingsCard>

      <SettingsCard title="Upgrade or Change Plan">
        <p className="text-sm text-slate-600 mb-4">
          Switch between monthly and annual billing, or start a subscription.
        </p>
        <Button
          variant="outline"
          onClick={() => navigate('/upgrade')}
          className="rounded-xl border-primary text-primary hover:bg-primary/5"
        >
          View Plans
        </Button>
      </SettingsCard>
    </div>
  );
}

// ── Security Tab ──────────────────────────────────────────────────────────────
function SecurityTab() {
  const { user } = useUser();
  const { openUserProfile } = useClerk();

  return (
    <div className="space-y-4">
      <SettingsCard title="Password & Security"
        description="Change your password or manage connected accounts.">
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Password changes and connected accounts (Google, Microsoft) are managed securely through our auth provider.
          </p>
          <Button onClick={() => openUserProfile()}
            variant="outline"
            className="rounded-xl border-slate-200 hover:bg-slate-50">
            Manage password & connected accounts
          </Button>
        </div>
      </SettingsCard>

      <SettingsCard title="Account Details">
        <div className="space-y-3 text-sm text-slate-600">
          <div className="flex justify-between">
            <span className="text-slate-500">Email verified</span>
            <span className="font-medium text-emerald-600">
              {user?.hasVerifiedEmailAddress ? '✓ Verified' : 'Not verified'}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Account created</span>
            <span className="font-medium text-slate-700">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '—'}
            </span>
          </div>
        </div>
      </SettingsCard>
    </div>
  );
}

// ── Support Tab ───────────────────────────────────────────────────────────────
function SupportTab() {
  const { user } = useUser();
  const [feedback, setFeedback] = useState('');
  const [sent, setSent] = useState(false);

  function handleSendFeedback() {
    if (!feedback.trim()) return;
    // Opens a pre-filled mailto with feedback
    const body = encodeURIComponent(`Feedback from ${user?.primaryEmailAddress?.emailAddress ?? 'a teacher'}:\n\n${feedback}`);
    window.open(`mailto:support@teachbud.app?subject=TeachBud Feedback&body=${body}`, '_blank');
    setSent(true);
    setFeedback('');
    setTimeout(() => setSent(false), 3000);
  }

  return (
    <div className="space-y-4">
      <SettingsCard title="Send Feedback"
        description="Found a bug? Have a suggestion? We'd love to hear from you.">
        <div className="space-y-3">
          <textarea
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            rows={4}
            placeholder="Tell us what's on your mind…"
            className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-900 placeholder:text-slate-400 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-none"
          />
          <div className="flex justify-between items-center">
            {sent && <span className="text-sm text-emerald-600 font-medium flex items-center gap-1.5"><CheckCircle2 size={14} /> Thanks for the feedback!</span>}
            <Button onClick={handleSendFeedback} disabled={!feedback.trim()} className="ml-auto rounded-xl bg-primary hover:bg-primary/90 text-white">
              Send Feedback
            </Button>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Help & Resources">
        <div className="space-y-3">
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="font-semibold text-slate-800 text-sm mb-1">Quick Tips</p>
            <ul className="text-slate-500 text-sm space-y-1 list-none">
              <li>→ Start with <strong>Lesson Planner</strong> to generate a complete lesson plan</li>
              <li>→ Use <strong>Activity Sheets</strong> and <strong>Presentations</strong> from any saved lesson</li>
              <li>→ Connect your <strong>Curriculum Hub</strong> to align lessons to your pacing guide</li>
              <li>→ All saved resources stay in <strong>My Resources</strong></li>
            </ul>
          </div>
        </div>
      </SettingsCard>

      <SettingsCard title="Legal">
        <div className="flex flex-col gap-3">
          <a
            href={`${import.meta.env.BASE_URL?.replace(/\/$/, '')}/privacy`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-primary transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
          >
            <Shield size={14} className="flex-shrink-0" />
            Privacy Policy
            <ExternalLink size={11} className="text-slate-400 ml-auto" />
          </a>
          <a
            href={`${import.meta.env.BASE_URL?.replace(/\/$/, '')}/terms`}
            target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-primary transition-colors
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-md"
          >
            <FileText size={14} className="flex-shrink-0" />
            Terms of Service
            <ExternalLink size={11} className="text-slate-400 ml-auto" />
          </a>
          <p className="text-xs text-slate-400 pt-1 border-t border-slate-100">
            Both documents are drafts pending attorney review before public launch.
          </p>
        </div>
      </SettingsCard>

      <SettingsCard title="Beta Launch Checklist" description="Admin-only. Run this before inviting beta teachers.">
        <PreflightChecklist />
      </SettingsCard>
    </div>
  );
}
